import type {
  IPrincipalInvestigatorRepository,
  InvestigatorType,
  PrincipalInvestigator,
  CreatePiRequest,
  UpdatePiRequest,
} from "../repositories/PrincipalInvestigatorRepository";

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface IInvestigatorUseCase {
  getInvestigatorTypes(includeInactive?: boolean): Promise<InvestigatorType[]>;
  getInvestigatorsForSite(
    siteId: string,
    includeInactive?: boolean,
  ): Promise<PrincipalInvestigator[]>;
  createInvestigator(
    siteId: string,
    data: CreatePiRequest,
  ): Promise<PrincipalInvestigator>;
  updateInvestigator(
    siteId: string,
    piId: string,
    data: UpdatePiRequest,
  ): Promise<PrincipalInvestigator>;
  linkInvestigatorToSite(
    siteId: string,
    piId: string,
    targetSiteId: string,
    makePrimary?: boolean,
  ): Promise<PrincipalInvestigator>;
  deleteInvestigator(siteId: string, piId: string): Promise<void>;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export class InvestigatorUseCase implements IInvestigatorUseCase {
  constructor(
    private readonly piRepository: IPrincipalInvestigatorRepository,
  ) {}

  async getInvestigatorTypes(
    includeInactive = false,
  ): Promise<InvestigatorType[]> {
    return this.piRepository.getInvestigatorTypes(includeInactive);
  }

  async getInvestigatorsForSite(
    siteId: string,
    includeInactive = true,
  ): Promise<PrincipalInvestigator[]> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    return this.piRepository.getPisForSite(siteId, includeInactive);
  }

  async createInvestigator(
    siteId: string,
    data: CreatePiRequest,
  ): Promise<PrincipalInvestigator> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!data.name?.trim()) {
      throw new Error("Investigator name is required");
    }
    return this.piRepository.createPi(siteId, data);
  }

  async updateInvestigator(
    siteId: string,
    piId: string,
    data: UpdatePiRequest,
  ): Promise<PrincipalInvestigator> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!piId?.trim()) {
      throw new Error("Investigator ID is required");
    }
    return this.piRepository.updatePi(siteId, piId, data);
  }

  async linkInvestigatorToSite(
    siteId: string,
    piId: string,
    targetSiteId: string,
    makePrimary = false,
  ): Promise<PrincipalInvestigator> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!piId?.trim()) {
      throw new Error("Investigator ID is required");
    }
    if (!targetSiteId?.trim()) {
      throw new Error("Target site ID is required");
    }
    return this.piRepository.linkPiToSite(
      siteId,
      piId,
      targetSiteId,
      makePrimary,
    );
  }

  async deleteInvestigator(siteId: string, piId: string): Promise<void> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!piId?.trim()) {
      throw new Error("Investigator ID is required");
    }
    return this.piRepository.deletePi(siteId, piId);
  }
}
